import { z } from 'zod';

/**
 * Connection state for the market-data feed. Drives the honest-price rule:
 * the UI must never render a live-looking price when the feed is not live.
 *  - live:         ticks flowing, prices trustworthy
 *  - offline:      transport down — device has no internet or the backend is
 *                  unreachable; actively retrying
 *  - reconnecting: connected to the backend, but its upstream market feed is
 *                  down; waiting for it to resume
 *  - stale:        connected but no tick for > STALE_AFTER_MS for some symbols
 *  - closed:       market closed; showing last close, not live
 */
export type ConnectionState = 'live' | 'offline' | 'reconnecting' | 'stale' | 'closed';

/**
 * Milliseconds without a tick after which a symbol's price is considered
 * stale. Kite pushes on trade, not on a clock — a liquid symbol legitimately
 * pauses several seconds in a quiet stretch, and 5s rejected real orders
 * mid-session. Shared by the app's swipe-lock and the server's order gate so
 * the two can never disagree about what counts as live.
 */
export const STALE_AFTER_MS = 10_000;

export type ExchangeSegment = 'NSE' | 'BSE' | 'NFO' | 'INDEX';

export const InstrumentKindSchema = z.enum(['equity', 'index', 'future', 'option']);
export type InstrumentKind = z.infer<typeof InstrumentKindSchema>;

export const InstrumentSchema = z.object({
  /** Trading symbol, e.g. "RELIANCE", "NIFTY 50", "RELIANCE 2960 CE". */
  symbol: z.string(),
  /** Human name, e.g. "Reliance Industries". */
  name: z.string(),
  exchange: z.custom<ExchangeSegment>(),
  kind: InstrumentKindSchema,
  /** Lot size for tradables; 1 for equities, undefined for indices. */
  lotSize: z.number().optional(),
  /** Exchange tick size in rupees (0.05 for most equities). Drives the
   *  chart's price scale and the ticket's price steps. */
  tickSize: z.number().optional(),
  /** F&O contracts: expiry (YYYY-MM-DD), strike, and CE/PE side — so the UI
   *  can render "NIFTY 24500 CE · 28 Jul" instead of the raw tradingsymbol. */
  expiry: z.string().optional(),
  strike: z.number().optional(),
  optionType: z.enum(['CE', 'PE']).optional(),
  /** True when this underlying has a listed F&O chain (drives the tab set). */
  hasOptions: z.boolean().optional(),
  /** True when a BSE listing exists — the chart prefers TradingView's free
   *  BSE widget for these, and falls back to our own data when false. */
  bseListed: z.boolean().optional(),
});
export type Instrument = z.infer<typeof InstrumentSchema>;

/** One level of the 5-level order book. */
export const DepthLevelSchema = z.object({
  price: z.number(),
  qty: z.number(),
  orders: z.number(),
});
export type DepthLevel = z.infer<typeof DepthLevelSchema>;

/** Full quote from Kite: day OHLC, volume, circuits, live 5-level depth. */
export const QuoteSchema = z.object({
  symbol: z.string(),
  lastPrice: z.number(),
  open: z.number(),
  high: z.number(),
  low: z.number(),
  prevClose: z.number(),
  volume: z.number(),
  avgPrice: z.number(),
  upperCircuit: z.number().nullable(),
  lowerCircuit: z.number().nullable(),
  /** Open interest — F&O only. */
  oi: z.number().nullable(),
  /** 52-week range, from cached weekly candles; null until first computed. */
  week52High: z.number().nullable(),
  week52Low: z.number().nullable(),
  depth: z.object({ buy: z.array(DepthLevelSchema), sell: z.array(DepthLevelSchema) }),
});
export type Quote = z.infer<typeof QuoteSchema>;

/** OHLCV candle, epoch seconds. */
export const CandleSchema = z.object({
  t: z.number(),
  o: z.number(),
  h: z.number(),
  l: z.number(),
  c: z.number(),
  v: z.number(),
});
export type Candle = z.infer<typeof CandleSchema>;

export const CandlesResponseSchema = z.object({
  candles: z.array(CandleSchema),
  interval: z.string(),
});
export type CandlesResponse = z.infer<typeof CandlesResponseSchema>;

/** One strike row of the option chain — real NFO tradingsymbols. */
export const ChainRowSchema = z.object({
  strike: z.number(),
  ce: z.string().nullable(),
  pe: z.string().nullable(),
});
export type ChainRow = z.infer<typeof ChainRowSchema>;

export const OptionChainSchema = z.object({
  underlying: z.string(),
  /** Contract expiry (YYYY-MM-DD) this chain is for — nearest by default. */
  expiry: z.string(),
  /** All listed expiries (soonest first) — drives the expiry tabs. */
  expiries: z.array(z.string()),
  lotSize: z.number(),
  spot: z.number().nullable(),
  rows: z.array(ChainRowSchema),
});
export type OptionChain = z.infer<typeof OptionChainSchema>;

export type MarketPhase = 'preopen' | 'open' | 'closed';
export const MarketStatusSchema = z.object({
  phase: z.custom<MarketPhase>(),
  /** Chip label: "NSE OPEN", "PRE-OPEN", "NSE CLOSED". */
  label: z.string(),
  /** Whether orders can execute now (AMO still accepted when closed). */
  tradable: z.boolean(),
  /** Next open, ISO — shown on the market-closed state (screen 16). */
  nextOpen: z.string().nullable(),
  /** Server clock in IST, so the client shows off-hours time correctly. */
  serverTimeIst: z.string(),
});
export type MarketStatus = z.infer<typeof MarketStatusSchema>;

/**
 * A single normalized market tick. Source-agnostic by design — the same shape
 * whether it originates from Kite, an authorised vendor, delayed data, or the mock.
 */
export const TickSchema = z.object({
  symbol: z.string(),
  /** Last traded price, in rupees. */
  ltp: z.number(),
  /** Absolute change from previous close, in rupees. */
  change: z.number(),
  /** Change as a fraction (0.0065 = +0.65%). */
  changePct: z.number(),
  /**
   * Best bid/ask, in rupees — what a seller receives / a buyer pays right now.
   * The spread between them is real money on options, where LTP alone made
   * buying and selling look like the same price. Absent for indices (nothing
   * to trade) and for sources that carry no depth; consumers fall back to LTP.
   */
  bid: z.number().optional(),
  ask: z.number().optional(),
  /**
   * Up to five levels per side of the visible order book, best first —
   * price AND quantity. This is what a market order actually matches
   * against: a size bigger than the touch walks these levels at worse
   * prices, exactly as the exchange fills it. Carried on the internal tick
   * bus for the fill engine; the WS gateway strips it before phone fanout
   * (clients only render the touch, and depth triples the frame size).
   */
  depth: z
    .object({
      buy: z.array(z.object({ price: z.number(), qty: z.number() })),
      sell: z.array(z.object({ price: z.number(), qty: z.number() })),
    })
    .optional(),
  /** Epoch millis when this tick was produced. Drives per-symbol staleness. */
  at: z.number(),
});
export type Tick = z.infer<typeof TickSchema>;

export type Unsubscribe = () => void;

/**
 * The one seam the whole app hinges on. Screens subscribe to symbols and receive
 * ticks; they never know or care where the data comes from. Swapping Kite for an
 * authorised vendor, delayed data, or the mock is a matter of changing which
 * implementation is provided — no screen changes.
 */
export interface MarketDataSource {
  subscribe(symbols: string[]): void;
  unsubscribe(symbols: string[]): void;
  /** Register a tick listener. Returns an unsubscribe fn. */
  onTick(cb: (tick: Tick) => void): Unsubscribe;
  /** Register a connection-state listener. Fires immediately with current state. */
  onConnectionState(cb: (state: ConnectionState) => void): Unsubscribe;
  connectionState(): ConnectionState;
  /** Epoch millis of the last tick for a symbol, or null if never seen. */
  lastTickAt(symbol: string): number | null;
}
